import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

const http = axios.create({ baseURL: API, withCredentials: true });

// On 401, try a one-time refresh (refresh cookie lasts 30d) so sessions persist
// across access-token expiry. If refresh fails, redirect to login.
let _refreshing = null;
http.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err?.config || {};
    const status = err?.response?.status;
    const url = original.url || "";
    if (status === 402) {
      const msg = typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "";
      window.dispatchEvent(new CustomEvent("vozelto:plan-limit", { detail: { message: msg } }));
    }
    const isAuthEndpoint = url.includes("/auth/");
    if (status === 401 && !isAuthEndpoint && !original._retry) {
      original._retry = true;
      try {
        _refreshing = _refreshing || http.post("/auth/refresh");
        await _refreshing;
        _refreshing = null;
        return http(original);
      } catch (e) {
        _refreshing = null;
        const path = window.location.pathname;
        const publicPath = path === "/login" || path.startsWith("/accept-invite") ||
          path.startsWith("/forgot-password") || path.startsWith("/reset-password");
        if (!publicPath) window.location.href = "/";
        return Promise.reject(err);
      }
    }
    return Promise.reject(err);
  }
);

// Auth
export const setupStatus = () => http.get("/auth/setup-status").then((r) => r.data);
export const setupOwner = (body) => http.post("/auth/setup", body).then((r) => r.data);
export const forgotPassword = (email) => http.post("/auth/forgot-password", { email }).then((r) => r.data);
export const resetPassword = (token, password) => http.post("/auth/reset-password", { token, password }).then((r) => r.data);
export const updateProfile = (body) => http.put("/auth/profile", body).then((r) => r.data);
export const authLogin = (email, password) => http.post("/auth/login", { email, password }).then((r) => r.data);
export const authLogout = () => http.post("/auth/logout").then((r) => r.data);
export const authMe = () => http.get("/auth/me").then((r) => r.data);
export const googleSession = (sessionId) => http.post("/auth/google/session", { session_id: sessionId }).then((r) => r.data);
export const inviteInfo = (token) => http.get(`/auth/invite/${token}`).then((r) => r.data);
export const acceptInvite = (token, password) => http.post("/auth/accept-invite", { token, password }).then((r) => r.data);
export const listUsers = () => http.get("/users").then((r) => r.data);
export const inviteUser = (body) => http.post("/users/invite", body).then((r) => r.data);
export const updateUser = (id, body) => http.put(`/users/${id}`, body).then((r) => r.data);
export const deleteUser = (id) => http.delete(`/users/${id}`).then((r) => r.data);
export const resendInvite = (id) => http.post(`/users/${id}/resend-invite`).then((r) => r.data);
export const permsCatalog = () => http.get("/auth/permissions-catalog").then((r) => r.data);
export const auditLogs = (params = {}) => http.get("/audit-logs", { params }).then((r) => r.data);
export const agencyOverview = () => http.get("/agency/overview").then((r) => r.data);

// ---- AI Usage & Plan Controls ----
export const usageSummary = (clientId) =>
  http.get("/usage/summary", { params: { client_id: clientId } }).then((r) => r.data);
export const usageAdminOverview = (params = {}) =>
  http.get("/usage/admin/overview", { params }).then((r) => r.data);
export const usageAdminEvents = (clientId) =>
  http.get("/usage/admin/events", { params: { client_id: clientId } }).then((r) => r.data);
export const usageAdminAudit = (clientId) =>
  http.get("/usage/admin/audit", { params: clientId ? { client_id: clientId } : {} }).then((r) => r.data);
export const usageAdminAdjust = (clientId, body) =>
  http.put(`/usage/admin/client/${clientId}`, body).then((r) => r.data);
export const usagePlans = () => http.get("/usage/admin/plans").then((r) => r.data);
export const usageEditPlan = (planId, body) => http.put(`/usage/admin/plans/${planId}`, body).then((r) => r.data);
export const usageAppControls = () => http.get("/usage/admin/app-controls").then((r) => r.data);
export const usageSetAppControls = (body) => http.put("/usage/admin/app-controls", body).then((r) => r.data);
export const usageExportCsv = () => http.get("/usage/admin/export", { responseType: "blob" }).then((r) => r.data);
export const usageBillingEmails = () => http.get("/usage/admin/billing-emails").then((r) => r.data);
export const usageTestBillingEmail = (to) => http.post("/usage/admin/billing-emails/test", { to }).then((r) => r.data);

// ---- LinkedIn Marketing Center ----
export const liStatus = (clientId) => http.get("/linkedin/status", { params: { client_id: clientId } }).then((r) => r.data);
export const liConnectUrl = (clientId) => `${API}/linkedin/oauth/start?client_id=${clientId}`;
export const liTestConnect = (clientId) => http.post("/linkedin/test-connect", { client_id: clientId }).then((r) => r.data);
export const liSelectOrg = (clientId, urn) => http.post("/linkedin/select-org", { client_id: clientId, organization_urn: urn }).then((r) => r.data);
export const liDisconnect = (clientId) => http.post("/linkedin/disconnect", { client_id: clientId }).then((r) => r.data);
export const liGenerate = (body) => http.post("/linkedin/generate", body).then((r) => r.data);
export const liOptimize = (clientId, text) => http.post("/linkedin/optimize", { client_id: clientId, text }).then((r) => r.data);
export const liCompose = (body) => http.post("/linkedin/compose", body).then((r) => r.data);
export const liPublish = (approvalId) => http.post("/linkedin/publish", { approval_id: approvalId }).then((r) => r.data);
export const liPosts = (clientId) => http.get("/linkedin/posts", { params: { client_id: clientId } }).then((r) => r.data);
export const liAnalytics = (clientId, force = false) => http.get("/linkedin/analytics", { params: { client_id: clientId, force } }).then((r) => r.data);
export const liBestTimes = (clientId) => http.get("/linkedin/best-times", { params: { client_id: clientId } }).then((r) => r.data);

// ---- Client Media Library ----
export const mediaList = (clientId, params = {}) => http.get("/media", { params: { client_id: clientId, ...params } }).then((r) => r.data);
export const mediaUpload = (form, onProgress) => http.post("/media/upload", form, { headers: { "Content-Type": "multipart/form-data" }, onUploadProgress: onProgress }).then((r) => r.data);
export const mediaPatch = (id, body) => http.patch(`/media/${id}`, body).then((r) => r.data);
export const mediaDelete = (id) => http.delete(`/media/${id}`).then((r) => r.data);
export const mediaUsage = (id) => http.get(`/media/${id}/usage`).then((r) => r.data);
export const mediaAttach = (approvalId, mediaId) => http.post("/media/attach", { approval_id: approvalId, media_id: mediaId }).then((r) => r.data);
export const mediaEnhance = (id, action, params = {}, pairMediaId = null) => http.post(`/media/${id}/enhance`, { action, params, pair_media_id: pairMediaId }).then((r) => r.data);
export const mediaCaption = (mediaId, platform) => http.post("/media/caption", { media_id: mediaId, platform }).then((r) => r.data);
export const mediaSuggest = (clientId, postText) => http.post("/media/suggest", { client_id: clientId, post_text: postText }).then((r) => r.data);
export const mediaPrefs = (clientId) => http.get("/media/prefs", { params: { client_id: clientId } }).then((r) => r.data);
export const mediaSetPrefs = (body) => http.put("/media/prefs", body).then((r) => r.data);

export const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
export const fmtNum = (n) =>
  Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
export const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;

// Clients
export const getClients = () => http.get("/clients").then((r) => r.data);
export const getClient = (id) => http.get(`/clients/${id}`).then((r) => r.data);
export const createClient = (body) => http.post("/clients", body).then((r) => r.data);
export const updateClient = (id, body) => http.put(`/clients/${id}`, body).then((r) => r.data);

// Generic data
export const listData = (collection, clientId) =>
  http.get(`/data/${collection}`, { params: { client_id: clientId } }).then((r) => r.data);
export const createData = (collection, payload) =>
  http.post(`/data/${collection}`, payload).then((r) => r.data);
export const updateData = (collection, id, payload) =>
  http.put(`/data/${collection}/${id}`, payload).then((r) => r.data);
export const deleteData = (collection, id) =>
  http.delete(`/data/${collection}/${id}`).then((r) => r.data);
export const bulkImport = (collection, clientId, rows) =>
  http.post(`/data/${collection}/bulk`, { client_id: clientId, rows }).then((r) => r.data);
export const cleanupApprovals = (body) =>
  http.post("/approvals/cleanup", body).then((r) => r.data);

// Minimal CSV parser (handles quoted fields & commas)
export function parseCSV(text) {
  const rows = [];
  let field = "", row = [], inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') inQuotes = false;
      else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ",") { row.push(field); field = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      if (field !== "" || row.length) { row.push(field); rows.push(row); row = []; field = ""; }
    } else field += c;
  }
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  if (!rows.length) return [];
  const headers = rows[0].map((h) => h.trim());
  return rows.slice(1).filter((r) => r.some((c) => c.trim() !== "")).map((r) => {
    const obj = {};
    headers.forEach((h, idx) => { obj[h] = (r[idx] ?? "").trim(); });
    return obj;
  });
}

// Dashboard
export const getDashboard = (clientId) =>
  http.get("/dashboard/summary", { params: { client_id: clientId } }).then((r) => r.data);

// AI
export const getRecommendations = (clientId) =>
  http.post("/ai/recommendations", { client_id: clientId }).then((r) => r.data);
export const getSessions = (clientId, persona) =>
  http.get("/ai/sessions", { params: { client_id: clientId, ...(persona ? { persona } : {}) } }).then((r) => r.data);
export const getMessages = (sessionId) =>
  http.get("/ai/messages", { params: { session_id: sessionId } }).then((r) => r.data);
export const chatToApproval = (clientId, content, platform = "Facebook") =>
  http.post("/ai/chat-to-approval", { client_id: clientId, content, platform }).then((r) => r.data);

// AI Specialists (Sales / SEO / Advertising / Website)
export const getSpecialist = (key, clientId) =>
  http.get(`/specialists/${key}`, { params: { client_id: clientId } }).then((r) => r.data);
export const specialistAction = (key, body) =>
  http.post(`/specialists/${key}/action`, body).then((r) => r.data);

// Brand Assets (single source of truth)
export const brandAssetsMeta = () => http.get("/brand-assets/meta").then((r) => r.data);
export const listBrandAssets = (clientId, params = {}) =>
  http.get("/brand-assets", { params: { client_id: clientId, ...params } }).then((r) => r.data);
export const uploadBrandAsset = (formData) =>
  http.post("/brand-assets/upload", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
export const createTextAsset = (body) => http.post("/brand-assets/text", body).then((r) => r.data);
export const patchBrandAsset = (id, body) => http.patch(`/brand-assets/${id}`, body).then((r) => r.data);
export const deleteBrandAsset = (id) => http.delete(`/brand-assets/${id}`).then((r) => r.data);
export const brandAssetVersions = (id) => http.get(`/brand-assets/${id}/versions`).then((r) => r.data);
export const brandAssetRecs = (clientId) =>
  http.get(`/brand-assets/recommendations/${clientId}`).then((r) => r.data);

// Templates
export const listTemplates = (clientId, params = {}) =>
  http.get("/templates", { params: { client_id: clientId, ...params } }).then((r) => r.data);
export const saveCustomTemplate = (body) => http.post("/templates/custom", body).then((r) => r.data);
export const deleteCustomTemplate = (id) => http.delete(`/templates/custom/${id}`).then((r) => r.data);
export const toggleTemplateFavorite = (clientId, templateId) =>
  http.post("/templates/favorite", { client_id: clientId, template_id: templateId }).then((r) => r.data);
export const launchTemplate = (body) => http.post("/templates/launch", body).then((r) => r.data);

// Shopify integration
export const shopifyStatus = (clientId) =>
  http.get("/integrations/shopify/status", { params: { client_id: clientId } }).then((r) => r.data);
export const shopifyConnect = (clientId, store_domain, api_client_id, api_client_secret) =>
  http.post("/integrations/shopify/connect", { client_id: clientId, store_domain, api_client_id, api_client_secret }).then((r) => r.data);
export const shopifyOAuthStart = (clientId, store_domain, api_client_id, api_client_secret) =>
  http.post("/integrations/shopify/oauth/start", {
    client_id: clientId, store_domain, api_client_id, api_client_secret,
    redirect_uri: `${API}/integrations/shopify/callback`,
    return_url: window.location.origin,
  }).then((r) => r.data);
export const shopifySync = (clientId) =>
  http.post("/integrations/shopify/sync", { client_id: clientId }).then((r) => r.data);
export const shopifyDisconnect = (clientId) =>
  http.post("/integrations/shopify/disconnect", { client_id: clientId }).then((r) => r.data);
export const shopifyVerifyScopes = (clientId) =>
  http.post("/integrations/shopify/verify-scopes", { client_id: clientId }).then((r) => r.data);
export const shopifyReauthorize = (clientId) =>
  http.post("/integrations/shopify/oauth/reauthorize", {
    client_id: clientId,
    redirect_uri: `${API}/integrations/shopify/callback`,
    return_url: window.location.origin,
  }).then((r) => r.data);
export const shopifyData = (resource, clientId) =>
  http.get(`/integrations/shopify/data/${resource}`, { params: { client_id: clientId } }).then((r) => r.data);
export const shopifyPropose = (clientId, shopify_product_id, change_type) =>
  http.post("/integrations/shopify/propose-change", { client_id: clientId, shopify_product_id, change_type }).then((r) => r.data);

// Assets (brand artwork + reference images) — cloud object storage
export const uploadAsset = (clientId, file, category, kind = "reference") => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("client_id", clientId);
  fd.append("category", category);
  fd.append("kind", kind);
  return http.post("/assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const listAssets = (clientId, category) =>
  http.get("/assets", { params: { client_id: clientId, category } }).then((r) => r.data);
export const deleteAsset = (assetId) =>
  http.delete(`/assets/${assetId}`).then((r) => r.data);
export const activateAsset = (assetId) =>
  http.post(`/assets/${assetId}/activate`).then((r) => r.data);
export const renameAsset = (assetId, display_name) =>
  http.patch(`/assets/${assetId}`, { display_name }).then((r) => r.data);
export const getBrandSettings = (clientId) =>
  http.get("/brand/settings", { params: { client_id: clientId } }).then((r) => r.data);
export const putBrandSettings = (settings) =>
  http.put("/brand/settings", settings).then((r) => r.data);

// Generated image library
export const listLibrary = (clientId, params = {}) =>
  http.get("/library", { params: { client_id: clientId, ...params } }).then((r) => r.data);
export const patchLibrary = (id, body) =>
  http.patch(`/library/${id}`, body).then((r) => r.data);
export const deleteLibrary = (id) =>
  http.delete(`/library/${id}`).then((r) => r.data);
export const duplicateLibrary = (id) =>
  http.post(`/library/${id}/duplicate`).then((r) => r.data);
export const uploadMedia = (clientId, file, title = "", tags = "") => {
  const fd = new FormData();
  fd.append("file", file); fd.append("client_id", clientId);
  fd.append("title", title); fd.append("tags", tags);
  return http.post("/library/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

// Brand profile (Brand Manager)
export const getBrandProfile = (clientId) =>
  http.get("/brand/profile", { params: { client_id: clientId } }).then((r) => r.data);
export const putBrandProfile = (profile) =>
  http.put("/brand/profile", profile).then((r) => r.data);
export const getBrandKit = (clientId) =>
  http.get("/brand/kit", { params: { client_id: clientId } }).then((r) => r.data);
export const putBrandKit = (body) =>
  http.put("/brand/kit", body).then((r) => r.data);
export const getCategoryProfile = (clientId) =>
  http.get("/ai/brand-profile", { params: { client_id: clientId } }).then((r) => r.data);
export const addPromptPreset = (body) =>
  http.post("/brand/presets", body).then((r) => r.data);
export const deletePromptPreset = (body) =>
  http.post("/brand/presets/delete", body).then((r) => r.data);
export const imageIntel = (clientId) =>
  http.get("/ai/image-intel", { params: { client_id: clientId } }).then((r) => r.data);
export const agencyCampaign = (body) =>
  http.post("/ai/agency-campaign", body).then((r) => r.data);
export const agencyCampaignStatus = (jobId) =>
  http.get(`/ai/agency-campaign/status/${jobId}`).then((r) => r.data);

// Billing (Stripe)
export const getBillingPlans = () => http.get("/billing/plans").then((r) => r.data);
export const getBillingStatus = (workspaceId) =>
  http.get("/billing/status", { params: { workspace_id: workspaceId } }).then((r) => r.data);
export const startCheckout = (body) => http.post("/billing/checkout", body).then((r) => r.data);
export const getCheckoutStatus = (sessionId) =>
  http.get(`/billing/status/${sessionId}`).then((r) => r.data);
export const billingSelfTest = () => http.get("/billing/self-test").then((r) => r.data);
export const billingDiagnostics = () => http.get("/billing/diagnostics").then((r) => r.data);
export const billingPortal = (workspace_id, return_url) =>
  http.post("/billing/portal", { workspace_id, return_url }).then((r) => r.data);
export const registerAccount = (body) => http.post("/auth/register", body).then((r) => r.data);
export const trialCheckout = (body) => http.post("/billing/trial-checkout", body).then((r) => r.data);

// Klaviyo
export const getKlaviyoStatus = (clientId) =>
  http.get("/klaviyo/status", { params: { client_id: clientId } }).then((r) => r.data);
export const getKlaviyoFlows = (clientId) =>
  http.get("/klaviyo/flows", { params: { client_id: clientId } }).then((r) => r.data);
export const connectKlaviyo = (body) => http.post("/klaviyo/connect", body).then((r) => r.data);
export const draftKlaviyoFlow = (body) => http.post("/klaviyo/draft-flow", body).then((r) => r.data);
export const getKlaviyoOverview = (clientId) =>
  http.get("/klaviyo/overview", { params: { client_id: clientId } }).then((r) => r.data);
export const verifyKlaviyo = (clientId) =>
  http.get("/klaviyo/verify", { params: { client_id: clientId } }).then((r) => r.data);

// AI text tools
export const genCaptions = (clientId, topic, platform, tone, length) =>
  http.post("/ai/captions", { client_id: clientId, topic, platform, tone, length }).then((r) => r.data);
export const marketingSuggestions = (clientId, content_description, product_type) =>
  http.post("/ai/marketing-suggestions", { client_id: clientId, content_description, product_type }).then((r) => r.data);

// AI Image Studio V2 (Creative Director)
export const imageProducts = (clientId) =>
  http.get("/ai/image-products", { params: { client_id: clientId } }).then((r) => r.data);
export const generateImageBatch = (body) =>
  http.post("/ai/generate-image-batch", body, { timeout: 30000 }).then((r) => r.data);
export const improveVariation = (body) =>
  http.post("/ai/improve-variation", body, { timeout: 30000 }).then((r) => r.data);
export const qcImprove = (approvalId) =>
  http.post("/qc/improve", { approval_id: approvalId }, { timeout: 30000 }).then((r) => r.data);
export const qcApplyRevision = (approvalId, revisionId) =>
  http.post("/qc/apply-revision", { approval_id: approvalId, revision_id: revisionId }).then((r) => r.data);
export const qcReadiness = (approvalId) =>
  http.post("/qc/readiness", { approval_id: approvalId }, { timeout: 30000 }).then((r) => r.data);
export const imageJobStatus = (jobId) =>
  http.get(`/ai/image-job/${jobId}`, { timeout: 15000 }).then((r) => r.data);
export const pollImageJob = async (jobId, onProgress) => {
  for (let i = 0; i < 200; i++) {
    await new Promise((res) => setTimeout(res, 3000));
    let job;
    try {
      job = await imageJobStatus(jobId);
    } catch {
      continue; // transient network blip — keep polling
    }
    if (onProgress) onProgress(job);
    if (job.status === "done" || job.status === "error") return job;
  }
  return { status: "error", error: "Generation timed out — please try again" };
};
export const imageSendApproval = (body) =>
  http.post("/ai/image-send-approval", body).then((r) => r.data);
export const referenceGenerate = (formData) =>
  http.post("/ai/reference-generate", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 30000 }).then((r) => r.data);
export const referenceStatus = (jobId) =>
  http.get(`/ai/reference-status/${jobId}`, { timeout: 15000 }).then((r) => r.data);
export const imageToSocial = (clientId, imagePath, platform) =>
  http.post("/ai/image-to-social", { client_id: clientId, image_path: imagePath, platform }).then((r) => r.data);
export const imageWorkflow = (body) =>
  http.post("/ai/image-workflow", body, { timeout: 60000 }).then((r) => r.data);
export const createCampaignPack = (body) =>
  http.post("/ai/create-campaign-pack", body, { timeout: 30000 }).then((r) => r.data);
export const campaignPackage = (body) =>
  http.post("/ai/campaign-package", body, { timeout: 30000 }).then((r) => r.data);

// Campaign Engine / AI Marketing Director
export const runDirector = (clientId, command) =>
  http.post("/campaigns/director", { client_id: clientId, command }).then((r) => r.data);
export const listCampaigns = (clientId) =>
  http.get("/campaigns", { params: { client_id: clientId } }).then((r) => r.data);
export const getCampaign = (campaignId) =>
  http.get(`/campaigns/${campaignId}`).then((r) => r.data);
export const generateDeliverableImage = (campaignId, deliverableId, force = false) =>
  http.post(`/campaigns/${campaignId}/deliverables/${deliverableId}/generate`, null, { params: { force } }).then((r) => r.data);
export const generateAllAssets = (campaignId) =>
  http.post(`/campaigns/${campaignId}/generate-all`).then((r) => r.data);
export const regenerateApproval = (approvalId, instructions = "", mode = "fresh") =>
  http.post(`/campaigns/deliverable/${approvalId}/regenerate`, { instructions, mode }).then((r) => r.data);
export const regeneratePost = (approvalId) =>
  http.post(`/campaigns/deliverable/${approvalId}/regenerate-post`).then((r) => r.data);
export const campaignSummary = (campaignId) =>
  http.get(`/campaigns/${campaignId}/summary`).then((r) => r.data);
export const campaignHistory = (clientId) =>
  http.get("/campaigns/history/list", { params: { client_id: clientId } }).then((r) => r.data);
export const duplicateCampaign = (campaignId) =>
  http.post(`/campaigns/${campaignId}/duplicate`).then((r) => r.data);
export const scoreAsset = (approvalId) =>
  http.post(`/intel/score/${approvalId}`).then((r) => r.data);
export const campaignIntelligence = (campaignId, regenerate = false) =>
  http.post(`/intel/campaign-intelligence/${campaignId}`, null, { params: { regenerate } }).then((r) => r.data);
export const videoFromPost = (approvalId) =>
  http.post(`/video/from-post/${approvalId}`).then((r) => r.data);
export const videoJobStatus = (jobId) =>
  http.get(`/video/status/${jobId}`).then((r) => r.data);

// Analytics — GA4 + Ads (read-only) + Executive rollup
export const analyticsStatus = (clientId) =>
  http.get("/analytics/status", { params: { client_id: clientId } }).then((r) => r.data);
export const analyticsTest = (clientId, provider) =>
  http.post("/analytics/test", { client_id: clientId, provider }).then((r) => r.data);
export const connectionsHealth = (clientId, force = false) =>
  http.get("/health/connections", { params: { client_id: clientId, force } }).then((r) => r.data);
export const systemReadiness = (clientId, force = false) =>
  http.get("/health/system", { params: { client_id: clientId, force } }).then((r) => r.data);
export const metaAdsStatus = (clientId) =>
  http.get("/metaads/status", { params: { client_id: clientId } }).then((r) => r.data);
export const metaAdsStart = (clientId) =>
  http.get("/metaads/oauth/start", { params: { client_id: clientId } }).then((r) => r.data);
export const metaAdsDiscover = (clientId) =>
  http.post("/metaads/discover", { client_id: clientId }).then((r) => r.data);
export const metaAdsSelect = (clientId, accountId) =>
  http.post("/metaads/select-account", { client_id: clientId, account_id: accountId }).then((r) => r.data);
export const metaAdsDisconnect = (clientId) =>
  http.post("/metaads/disconnect", { client_id: clientId }).then((r) => r.data);
export const googleAdsStatus = (clientId) =>
  http.get("/googleads/status", { params: { client_id: clientId } }).then((r) => r.data);
export const googleAdsStart = (clientId) =>
  http.get("/googleads/oauth/start", { params: { client_id: clientId } }).then((r) => r.data);
export const googleAdsDiscover = (clientId) =>
  http.post("/googleads/discover", { client_id: clientId }).then((r) => r.data);
export const googleAdsSelect = (clientId, customerId) =>
  http.post("/googleads/select-account", { client_id: clientId, customer_id: customerId }).then((r) => r.data);
export const googleAdsDisconnect = (clientId) =>
  http.post("/googleads/disconnect", { client_id: clientId }).then((r) => r.data);
export const analyticsConnect = (clientId, provider, credentials) =>
  http.post("/analytics/connect", { client_id: clientId, provider, credentials }).then((r) => r.data);
export const analyticsDisconnect = (clientId, provider) =>
  http.post("/analytics/disconnect", { client_id: clientId, provider }).then((r) => r.data);
export const ga4Summary = (clientId, start, end) =>
  http.get("/analytics/ga4/summary", { params: { client_id: clientId, start, end } }).then((r) => r.data);
export const adsSummary = (clientId, { platform = "all", campaign, start, end } = {}) =>
  http.get("/analytics/ads/summary", { params: { client_id: clientId, platform, campaign, start, end } }).then((r) => r.data);
export const executiveSummary = (clientId) =>
  http.get("/analytics/executive", { params: { client_id: clientId } }).then((r) => r.data);
export const ownerRollup = () =>
  http.get("/analytics/owner-rollup").then((r) => r.data);

// Deployment Validation (super admin only)
export const runDeployValidation = () => http.post("/deploy-validation/run", {}, { timeout: 120000 }).then((r) => r.data);
export const deployValidationHistory = () => http.get("/deploy-validation/history").then((r) => r.data);
export const lastDeployValidation = () => http.get("/deploy-validation/last").then((r) => r.data);

// Industry-aware dashboard vocabulary — labels only, data always comes from the workspace itself
export const bizTerms = (client) => {
  const ind = `${client?.industry || ""} ${client?.name || ""}`.toLowerCase();
  if (/(property|maintenance|landscap|plumb|hvac|clean|repair|local service)/.test(ind))
    return { orders: "Jobs", topProducts: "Top Services", bestSellers: "Top Services", aov: "Avg Job Value", customers: "Customers", unit: "jobs" };
  if (/(fitness|coach|gym|wellness|nutrition|training)/.test(ind))
    return { orders: "Memberships", topProducts: "Top Programs", bestSellers: "Top Programs", aov: "Avg Membership Value", customers: "Members", unit: "memberships" };
  return { orders: "Orders", topProducts: "Top Products", bestSellers: "Best Sellers", aov: "Avg Order Value", customers: "Customers", unit: "orders" };
};

// Product Intelligence (live Shopify)
export const productIntelDashboard = (clientId, params = {}) =>
  http.get("/product-intel/dashboard", { params: { client_id: clientId, ...params } }).then((r) => r.data);
export const productIntelAction = (body) =>
  http.post("/product-intel/action", body).then((r) => r.data);
export const productIntelBulkAction = (body) =>
  http.post("/product-intel/bulk-action", body).then((r) => r.data);

// Social publishing (Meta — Facebook Page + Instagram Business)
export const socialStatus = (clientId) =>
  http.get("/social/status", { params: { client_id: clientId } }).then((r) => r.data);
export const socialConnect = (body) => http.post("/social/connect", body).then((r) => r.data);
export const socialSelectPage = (clientId, pageId) =>
  http.post("/social/select-page", { client_id: clientId, page_id: pageId }).then((r) => r.data);
export const socialRefreshPages = (clientId) =>
  http.post("/social/refresh-pages", { client_id: clientId }).then((r) => r.data);
export const socialDebugLive = (clientId) =>
  http.get("/social/debug-live", { params: { client_id: clientId } }).then((r) => r.data);
export const socialAddPageById = (clientId, pageId) =>
  http.post("/social/add-page-by-id", { client_id: clientId, page_id: pageId }).then((r) => r.data);
export const socialSelect = (body) => http.post("/social/select", body).then((r) => r.data);
export const socialDisconnect = (clientId) =>
  http.post("/social/disconnect", { client_id: clientId }).then((r) => r.data);
export const socialGenerate = (body) => http.post("/social/generate", body, { timeout: 30000 }).then((r) => r.data);
export const socialGenerateStatus = (jobId) => http.get(`/social/generate-status/${jobId}`, { timeout: 15000 }).then((r) => r.data);
export const socialBatchPlan = (body) => http.post("/social/batch-plan", body).then((r) => r.data);
export const socialPublish = (approvalId, scheduledTime = null) =>
  http.post("/social/publish", { approval_id: approvalId, scheduled_time: scheduledTime }).then((r) => r.data);
export const socialActivity = (clientId) =>
  http.get("/social/activity", { params: { client_id: clientId } }).then((r) => r.data);

// AI Communications Manager
export const commsStatus = (clientId) => http.get("/comms/status", { params: { client_id: clientId } }).then((r) => r.data);
export const commsConnect = (body) => http.post("/comms/connect", body).then((r) => r.data);
export const commsDisconnect = (clientId) => http.post("/comms/disconnect", { client_id: clientId }).then((r) => r.data);
export const commsInbox = (clientId, category = "all") => http.get("/comms/inbox", { params: { client_id: clientId, category } }).then((r) => r.data);
export const commsDashboard = (clientId) => http.get("/comms/dashboard", { params: { client_id: clientId } }).then((r) => r.data);
export const commsSummary = (clientId) => http.get("/comms/summary", { params: { client_id: clientId } }).then((r) => r.data);
export const commsDraftReply = (clientId, emailId) => http.post("/comms/draft-reply", { client_id: clientId, email_id: emailId }).then((r) => r.data);
export const commsEmailAction = (clientId, emailId, action) => http.post("/comms/email-action", { client_id: clientId, email_id: emailId, action }).then((r) => r.data);

// AI Revenue Manager
export const revenueDashboard = (clientId) => http.get("/revenue/dashboard", { params: { client_id: clientId } }).then((r) => r.data);
export const revenueAction = (body) => http.post("/revenue/action", body).then((r) => r.data);

// AI Employees
export const aiEmployees = (clientId) => http.get("/ai-employees", { params: { client_id: clientId } }).then((r) => r.data);
export const aiEmployeeState = (body) => http.post("/ai-employees/state", body).then((r) => r.data);
export const dailyBrief = (clientId, owner_name = "Tom") =>
  http.post("/ai/daily-brief", { client_id: clientId, owner_name }).then((r) => r.data);

// Shopify approval-based WRITE system
export const shopifyWriteActions = () =>
  http.get("/integrations/shopify/write/actions").then((r) => r.data);
export const shopifyWritePropose = (body) =>
  http.post("/integrations/shopify/write/propose", body).then((r) => r.data);
export const shopifyWriteApply = (approvalId) =>
  http.post(`/integrations/shopify/write/${approvalId}/apply`).then((r) => r.data);
export const shopifyWriteActivity = (clientId) =>
  http.get("/integrations/shopify/write/activity", { params: { client_id: clientId } }).then((r) => r.data);

// AI Monthly Planner
export const monthlyGenerate = (body) => http.post("/monthly/generate", body).then((r) => r.data);
export const monthlyStatus = (jobId) => http.get(`/monthly/status/${jobId}`).then((r) => r.data);

// Content Strategy (Social Manager V2)
export const strategyMeta = () => http.get("/strategy/meta").then((r) => r.data);export const strategyGet = (clientId) => http.get("/strategy", { params: { client_id: clientId } }).then((r) => r.data);
export const strategySave = (body) => http.put("/strategy", body).then((r) => r.data);
export const strategyDetectIndustry = (clientId) => http.post("/strategy/detect-industry", { client_id: clientId }).then((r) => r.data);
export const strategyAnalytics = (clientId, month) => http.get("/strategy/analytics", { params: { client_id: clientId, month } }).then((r) => r.data);
export const strategyFillWeek = (clientId) => http.post("/strategy/fill-week", { client_id: clientId }).then((r) => r.data);

// Growth Engine — demo requests, leads pipeline, stories & testimonials
export const demoRequest = (body) => http.post("/public/demo-request", body).then((r) => r.data);
export const publicTestimonials = () => http.get("/public/testimonials").then((r) => r.data);
export const publicStories = () => http.get("/public/success-stories").then((r) => r.data);
export const growthLeads = (params = {}) => http.get("/growth/leads", { params }).then((r) => r.data);
export const growthLeadUpdate = (id, body) => http.patch(`/growth/leads/${id}`, body).then((r) => r.data);
export const growthLeadNote = (id, text) => http.post(`/growth/leads/${id}/notes`, { text }).then((r) => r.data);
export const growthLeadTask = (id, body) => http.post(`/growth/leads/${id}/tasks`, body).then((r) => r.data);
export const growthTaskUpdate = (id, taskId, body) => http.patch(`/growth/leads/${id}/tasks/${taskId}`, body).then((r) => r.data);
export const growthNotifications = () => http.get("/growth/notifications").then((r) => r.data);
export const growthNotificationsRead = () => http.post("/growth/notifications/read").then((r) => r.data);
export const growthMetrics = () => http.get("/growth/metrics").then((r) => r.data);
export const growthStories = () => http.get("/growth/stories").then((r) => r.data);
export const growthStorySave = (body) => http.post("/growth/stories", body).then((r) => r.data);
export const growthStoryDelete = (id) => http.delete(`/growth/stories/${id}`).then((r) => r.data);
export const growthTestimonials = () => http.get("/growth/testimonials").then((r) => r.data);
export const growthTestimonialSave = (body) => http.post("/growth/testimonials", body).then((r) => r.data);
export const growthTestimonialDelete = (id) => http.delete(`/growth/testimonials/${id}`).then((r) => r.data);

// Sales AI Toolkit / Website Analysis / Business Audit / Resources (Batch B)
export const salesTools = () => http.get("/specialists/sales/tools").then((r) => r.data);
export const runSalesTool = (body) => http.post("/specialists/sales/tool", body).then((r) => r.data);
export const salesOutputs = (clientId) => http.get("/specialists/sales/outputs", { params: { client_id: clientId } }).then((r) => r.data);
export const analyzeWebsite = (body) => http.post("/specialists/website/analyze", body).then((r) => r.data);
export const latestWebsiteAnalysis = (clientId) => http.get("/specialists/website/analysis", { params: { client_id: clientId } }).then((r) => r.data);
export const runBusinessAudit = (clientId) => http.post("/specialists/business-audit", { client_id: clientId }).then((r) => r.data);
export const latestBusinessAudit = (clientId) => http.get("/specialists/business-audit/latest", { params: { client_id: clientId } }).then((r) => r.data);
export const listResources = (params = {}) => http.get("/templates/resources", { params }).then((r) => r.data);

// AI Onboarding Wizard
export const onboardingStart = (body) => http.post("/onboarding/start", body).then((r) => r.data);
export const onboardingStatus = (jobId) => http.get(`/onboarding/status/${jobId}`).then((r) => r.data);
export const onboardingLatest = (clientId) => http.get("/onboarding/latest", { params: { client_id: clientId } }).then((r) => r.data);

// Auto Pilot
export const autopilotStatus = (clientId) =>
  http.get("/autopilot/status", { params: { client_id: clientId } }).then((r) => r.data);
export const autopilotSet = (clientId, state) =>
  http.post("/autopilot/set", { client_id: clientId, state }).then((r) => r.data);
export const autopilotToday = (clientId) =>
  http.get("/autopilot/today", { params: { client_id: clientId } }).then((r) => r.data);
export const autopilotRetry = (approvalId) =>
  http.post("/autopilot/retry", { approval_id: approvalId }).then((r) => r.data);
export const regenerateItemImage = (approvalId) =>
  http.post("/autopilot/item-image/regenerate", { approval_id: approvalId }).then((r) => r.data);
export const libraryList = (clientId) =>
  http.get("/library", { params: { client_id: clientId } }).then((r) => r.data);
export const libraryUpload = (clientId, file) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("client_id", clientId);
  return http.post("/library/upload", fd).then((r) => r.data);
};

// Streaming helper (SSE over fetch POST)
export async function streamAI(path, body, onDelta, onMeta) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = "AI request failed";
    try { msg = (await res.json()).detail || msg; } catch { /* non-JSON error body */ }
    if (res.status === 402) {
      window.dispatchEvent(new CustomEvent("vozelto:plan-limit", { detail: { message: msg } }));
    }
    throw new Error(msg);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let streamError = null;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop();
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith("data:")) continue;
      try {
        const data = JSON.parse(line.slice(5).trim());
        if (data.content) onDelta?.(data.content);
        if (data.session_id && onMeta) onMeta(data);
        if (data.error) streamError = data.error;
      } catch (e) {
        /* ignore partial */
      }
    }
  }
  if (streamError) throw new Error(streamError);
}
